import { DataFileInterface, FileSourceEnum } from '@systemeio/file-manager'
import { Checkbox, FormInput, FormInputNumber, cents } from '@systemeio/ui'
import { ProductVariantInterface } from 'modules/products/types/product-variant-interface'
import React from 'react'
import { ImageUploaderWithPreviewImage } from 'shared/components/file-uploader/image-uploader-with-preview-image'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useMediaQuery from 'shared/hooks/use-media-query'
import { twMerge } from 'tailwind-merge'
import { TrashIcon } from '@systemeio/ui/icons'

interface MobileVariantsTableProps {
  variants: ProductVariantInterface[]
  setVariants: React.Dispatch<React.SetStateAction<ProductVariantInterface[]>>
  isDeleteAvailable: boolean | null
  variantsImages: DataFileInterface[]
}
function MobileVariantsTable({
  variants,
  setVariants,
  isDeleteAvailable,
  variantsImages,
}: MobileVariantsTableProps) {
  const { t } = useLocoTranslation()
  const { isSM } = useMediaQuery()

  const handleChangeVariantSelect = (variant: ProductVariantInterface) => (value: boolean) =>
    setVariants(prev => prev.map(v => (v.id === variant.id ? { ...v, selected: value } : v)))

  const handleChangeVariantSkuValue =
    (variantId: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setVariants(prev => prev.map(v => (v.id === variantId ? { ...v, sku: e.target.value } : v)))

  const handleChangeVariantPriceValue =
    (variantId: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setVariants(prev =>
        prev.map(v => (v.id === variantId ? { ...v, price: cents(e.target.value) } : v)),
      )

  const handleChangeVariantStock = (variantId: string) => (stock: number) =>
    setVariants(prev => prev.map(v => (v.id === variantId ? { ...v, stock } : v)))

  const setVariantImage = (variantId: string) => (image?: DataFileInterface) =>
    setVariants(prev => prev.map(v => (v.id === variantId ? { ...v, image: image } : v)))

  const isDisabled = (variant: ProductVariantInterface) => {
    if (isDeleteAvailable !== null) {
      return isDeleteAvailable ? (variant.deleted ? true : false) : variant.deleted ? false : true
    }
    return false
  }

  const handleMarkVariantAsDeleted = (variant: ProductVariantInterface) => (e: React.MouseEvent) =>
    setVariants(prev => prev.map(v => (v.id === variant.id ? { ...v, deleted: true } : v)))

  const baseColumnClassName = twMerge(isSM && 'w-auto')

  return (
    <VariantTableContainer>
      <div className={twMerge('col-start-1 mt-5 flex flex-col', !isSM && 'col-end-6')}>
        {variants.map(variant => {
          return (
            <div key={variant.id} className="my-2 flex h-[70px] items-center">
              <Checkbox
                onChange={handleChangeVariantSelect(variant)}
                value={variant?.selected ?? false}
                disabled={isDisabled(variant)}
              />
              <div className="mx-4">
                <ImageUploaderWithPreviewImage
                  labelImageUploader=""
                  aspectRatio={1}
                  previewClassName="w-[60px] h-[60px]"
                  value={variant.image}
                  source={FileSourceEnum.Product}
                  onSelect={setVariantImage(variant.id)}
                  onRemove={() => setVariantImage(variant.id)(undefined)}
                />
              </div>
            </div>
          )
        })}
      </div>
      <VariantScrollableContainer className={twMerge(isSM && 'col-start-3')}>
        <VariantScrollableColumn
          title={t('dashboard.products.variant.title')}
          columnClassName={baseColumnClassName}
        >
          {variants.map(variant => (
            <div key={variant.id} className="my-2 flex h-[70px] items-center justify-evenly">
              <div
                className={`text-text-primary overflow-x-auto whitespace-nowrap text-xl ${
                  variant.deleted && 'line-through'
                }`}
              >
                {variant.productOptionValues?.map(e => e.value).join('/')}
              </div>
            </div>
          ))}
        </VariantScrollableColumn>
        <VariantScrollableColumn
          title={t('dashboard.products.sku.title')}
          columnClassName={baseColumnClassName}
        >
          {variants.map(variant => (
            <div key={variant.id} className="mx-4 my-2 flex h-[70px] items-center">
              <FormInput
                value={variant.sku}
                placeholder="SKU"
                onChange={handleChangeVariantSkuValue(variant.id)}
                maxLength={256}
                disabled={variant.deleted}
              />
            </div>
          ))}
        </VariantScrollableColumn>
        <VariantScrollableColumn title={t('global.price')} columnClassName={baseColumnClassName}>
          {variants.map(variant => (
            <div key={variant.id} className="my-2 flex h-[70px] items-center">
              <FormInput
                value={variant?.price}
                placeholder="Variant price"
                onChange={handleChangeVariantPriceValue(variant.id)}
                maxLength={256}
                disabled={variant.deleted}
              />
            </div>
          ))}
        </VariantScrollableColumn>
        <VariantScrollableColumn
          title={t('dashboard.products.stock.header')}
          columnClassName={baseColumnClassName}
        >
          {variants.map(variant => (
            <div key={variant.id} className="my-2 flex h-[70px] items-center">
              <FormInputNumber
                value={variant?.stock || 0}
                placeholder={t('dashboard.products.variant_stock')}
                onChange={handleChangeVariantStock(variant.id)}
                disabled={variant.deleted}
                type={'integer'}
              />
            </div>
          ))}
        </VariantScrollableColumn>
        <VariantScrollableColumn columnClassName={twMerge('mt-5 w-1/4', baseColumnClassName)}>
          {variants.map(variant => (
            <div key={variant.id} className="my-2 ml-4 flex h-[70px] items-center">
              {!variant.deleted && <TrashIcon onClick={handleMarkVariantAsDeleted(variant)} />}
            </div>
          ))}
        </VariantScrollableColumn>
      </VariantScrollableContainer>
    </VariantTableContainer>
  )
}

export default MobileVariantsTable

interface VariantColumnProps {
  title?: string
  children: React.ReactNode
  columnClassName?: string
}

function VariantScrollableColumn({ title, children, columnClassName = '' }: VariantColumnProps) {
  return (
    <div className={`flex w-full shrink-0 justify-center ${columnClassName}`}>
      <div className="mx-2 flex w-full flex-col">
        {title && <div className="flex justify-center">{title}</div>}
        {children}
      </div>
    </div>
  )
}

function VariantScrollableContainer({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={twMerge(
        'relative col-start-6 col-end-13 flex w-full flex-col overflow-auto overflow-x-scroll',
        className,
      )}
    >
      <div className="relative flex snap-mandatory scroll-smooth">{children}</div>
    </div>
  )
}

function VariantTableContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex">
      <div className="grid w-full grid-cols-12 gap-2">{children}</div>
    </div>
  )
}
